import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { KpiUnitField } from '@/features/projects/create-kpi/components/kpi-unit-field';
import { useAutoSetRelativeKpiDefaults } from '@/features/projects/create-kpi/hooks/use-auto-set-relative-kpi-defaults';
import { CreateProjectHeader } from '@/features/projects/create-project/components/layouts/create-project-header';
import {
  CreateProjectStoreSetData,
  useCreateProjectStore,
} from '@/features/projects/create-project/store/use-create-project-store';
import { kpiEditorSchema } from '@/features/projects/create-project/validation/kpi-editor-schema';
import {
  FlowBackButton,
  FlowSkipStepButton,
} from '@/shared/components/flow/flow-actions';
import {
  FlowFormFields,
  FlowHeaderOptional,
} from '@/shared/components/flow/flow-fields';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/shared/components/ui/form';
import { LabelInput } from '@/shared/components/ui/label-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { KpiType } from '@/shared/types/api';
import {
  formatNumberWithDots,
  parseFormattedNumber,
} from '@/shared/utils/format';

interface CreateProjectKpiFormProps {
  onSuccess: () => void;
  onBack: () => void;
  onSkip: () => void;
  onChange: (data: CreateProjectStoreSetData) => void;
}

export const CreateProjectKpiForm = ({
  onSuccess,
  onBack,
  onSkip,
  onChange,
}: CreateProjectKpiFormProps) => {
  const { t } = useTranslation('project', {
    keyPrefix: 'KpiFormEditor',
  });

  const { t: tShared } = useTranslation('shared');
  const { t: tCommon } = useTranslation('common');

  const {
    data: { kpis },
  } = useCreateProjectStore();

  const formSchema = kpiEditorSchema(t);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      variant: 'standard' as const,
      name: '',
      type: '' as KpiType,
      unit: '',
      max_value: undefined,
    },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    onChange({
      kpis: [...(kpis || []), values],
    });
    onSuccess();
  }

  useAutoSetRelativeKpiDefaults(form);

  return (
    <>
      <CreateProjectHeader>
        <FlowSkipStepButton onClick={onSkip} />
      </CreateProjectHeader>
      <Card variant="form" wrapWithStars>
        <CardHeader>
          <FlowHeaderOptional />
          <CardTitle>{t('title')}</CardTitle>
        </CardHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <CardContent>
              <FlowFormFields>
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <LabelInput
                          label={t('form_field.name.name')}
                          placeholder={t('form_field.name.placeholder')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  name="type"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || ''}
                      >
                        <FormControl>
                          <SelectTrigger isInputTrigger>
                            <SelectValue
                              asLabelInput
                              label={t('form_field.type.name')}
                              placeholder={t('form_field.type.placeholder')}
                            >
                              {tShared(field.value)}
                            </SelectValue>
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.values(KpiType).map((type) => (
                            <SelectItem key={type} value={type}>
                              {tShared(type)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <KpiUnitField
                  name="unit"
                  label={t('form_field.unit.name')}
                  placeholder={t('form_field.unit.placeholder')}
                  control={form.control}
                  isRelative={form.watch('type') === KpiType.RELATIVE}
                />
                <FormField
                  control={form.control}
                  name="max_value"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <LabelInput
                          label={t('form_field.max_value.name')}
                          placeholder={t('form_field.max_value.placeholder')}
                          value={
                            field.value === undefined
                              ? ''
                              : formatNumberWithDots(field.value)
                          }
                          onChange={(e) => {
                            const parsed = parseFormattedNumber(e.target.value);
                            field.onChange(
                              e.target.value === '' ? undefined : parsed,
                            );
                          }}
                          onBlur={field.onBlur}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FlowFormFields>
            </CardContent>
            <CardFooter>
              <FlowBackButton onClick={onBack}>
                {tCommon('button.back')}
              </FlowBackButton>
              <Button
                className="w-40"
                variant={'greenGradient'}
                btnType="primary"
                type="submit"
              >
                {t('submit_button_label')}
              </Button>
            </CardFooter>
          </form>
        </Form>
      </Card>
    </>
  );
};
